import { LogInstanceToken } from '@evorait/pace-logging';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { NotificationService } from '../../services/notification/notification.service';
import { ValueHelperService } from '../../services/value-helper/value-helper.service';
import { CseMaterialModel, JsonCseMaterialModel, ODataCseMaterialOutModel } from '../low-level/cse-material.model';
import { MaterialCheckModel } from '../low-level/material-check.model';
import {
    JsonNotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
} from '../low-level/notification-identifier.model';
import { NotificationModel } from '../low-level/notification.model';
import { JsonRecipientModel, ODataRecipientOutModel, RecipientModel } from '../low-level/recipient.model';
import { NumberModelOptions } from '../options/number-model-options';
import { MultilineTextModel, ODataTextLineOutModel } from '../primitive/multiline-text.model';
import { NumberModel } from '../primitive/number.model';
import { TextModel } from '../primitive/text.model';
import { CompositeModel } from './composite.model';
import { CseMaterialListModel } from './cse-material-list.model';
import { ODataCseSalesTidInput } from './cse-sales-tid-input.model';
import { DropdownModel } from './dropdown.model';
import { RecipientListModel } from './recipient-list.model';

export class MaterialQuoteModel extends CompositeModel {
    public frontId: TextModel;
    public notificationNo: TextModel;
    public techIdentNumber: TextModel;
    public timeUsed: NumberModel;
    public timeNeed: NumberModel;
    public distance: NumberModel;
    public quoteTypes: DropdownModel<TextModel>;
    public contactPerson: TextModel;
    public quotationNo: TextModel;
    public materialList: CseMaterialListModel;
    public existingRecipients: RecipientListModel;
    public lTextSet: MultilineTextModel;
    public equipments: DropdownModel<TextModel>;
    public commCategory: string;
    public notificationModel: NotificationModel | undefined;

    /**
     * Prepare a model for the KV CSE sales modal. Everything that does not depend on an actual equipment (ksNumber) is
     * setup in here.
     *
     * @param responseData
     * @param notificationModel
     * @param materialSet
     */
    public constructor(
        responseData?: ODataCseSalesTidInput,
        notificationModel?: NotificationModel,
        materialSet?: MaterialCheckModel[],
    ) {
        super('MaterialQuoteModel');

        const injector = CustomInjector.getInjector();
        const log = injector.get(LogInstanceToken);
        const configService = injector.get(ConfigService);
        const valueHelperService = injector.get(ValueHelperService);
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);

        const materialEntries: CseMaterialModel[] = [];
        let equipmentDropdownEntries = [];
        const recipientEntries: RecipientModel[] = [];
        const maxRecipients = configService.getMaxRecipientEntries();

        this.notificationModel = notificationModel;

        this.frontId = <TextModel>this.set(new TextModel('FrontId', configService.getUserFrontId()));

        this.timeUsed = <NumberModel>this.set(new NumberModel('TimeUsedMinutes', 0));
        modelOptionsLoader.load(this.timeUsed, 'MaterialQuote', 'UsedTime');

        this.timeNeed = <NumberModel>this.set(new NumberModel('TimeNeedMinutes', 1));
        modelOptionsLoader.load(this.timeNeed, 'MaterialQuote', 'AdditionalTime');

        this.distance = <NumberModel>(
            this.set(
                new NumberModel(
                    'Kilometer',
                    undefined,
                    new NumberModelOptions({ unit: configService.getMatQuoteDistanceUnit() }),
                ),
            )
        );
        modelOptionsLoader.load(this.distance, 'MaterialQuote', 'OverallDistance');

        const quoteTypesList = valueHelperService
            .getQuoteType()
            .map((vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel('QuotationType', vh.code)));

        this.quoteTypes = <DropdownModel<TextModel>>(
            this.set(new DropdownModel<TextModel>('QuotationType', quoteTypesList))
        );

        modelOptionsLoader.load(this.quoteTypes, 'MaterialQuote', 'QuoteType');

        this.contactPerson = <TextModel>this.set(new TextModel('ContactPerson'));
        modelOptionsLoader.load(this.contactPerson, 'MaterialQuote', 'ContactPerson');

        this.quotationNo = <TextModel>this.set(new TextModel('QuotationNo'));
        this.commCategory = ConfigConstants.quotationCommCategory;

        this.lTextSet = <MultilineTextModel>this.set(new MultilineTextModel('MatQuoteLtextSet'));
        modelOptionsLoader.load(this.lTextSet, 'MaterialQuote', 'FreeText');

        if (!notificationModel) {
            if (!responseData) {
                throw new Error('Could not create sales quote model without notification and response data!');
            }

            this.notificationNo = <TextModel>this.set(new TextModel('NotificationNo', responseData.NotificationNo));
            equipmentDropdownEntries = responseData.NotiCreateEquipmentsSet.results.map(
                (equipment) =>
                    new DisplayDecorator<TextModel>(
                        equipment.Description,
                        new TextModel('Equipment', equipment.Equipment),
                    ),
            );
            this.techIdentNumber = <TextModel>(
                this.set(new TextModel('TechnicalIdentNo', responseData.NotiCreateEquipmentsSet.results[0].TechIdentNo))
            );

            if (responseData.EmailFaxSet && responseData.EmailFaxSet.results) {
                const maxEntries =
                    responseData.EmailFaxSet.results.length < maxRecipients
                        ? responseData.EmailFaxSet.results.length
                        : maxRecipients;
                for (let i = 0; i < maxEntries; ++i) {
                    recipientEntries.push(
                        new RecipientModel(responseData.EmailFaxSet.results[i].FaxEmail, this.commCategory),
                    );
                }
            }
            materialEntries.push(new CseMaterialModel('', undefined, '', ConfigConstants.PROCESS_INDICATOR.INVIVO));
        } else {
            this.notificationNo = <TextModel>(
                this.set(new TextModel('NotificationNo', notificationModel.identifier.qmNum))
            );
            equipmentDropdownEntries = notificationModel
                .getUniqueEquipmentsByEquipmentNumber()
                .map(
                    (equipment) =>
                        new DisplayDecorator<TextModel>(
                            equipment.equiShortDesc.value,
                            new TextModel('Equipment', equipment.equnr.value),
                        ),
                );

            const mainEquipment = notificationModel.getMainEquipment();
            if (!mainEquipment) {
                log.error(
                    'MaterialQuoteComponent: No Main Equipment available for notification ' +
                        notificationModel.identifier.toString(),
                );
            }
            this.techIdentNumber = <TextModel>(
                this.set(new TextModel('TechnicalIdentNo', mainEquipment ? mainEquipment.tidnr.value : 'NO_EQUIPMENT'))
            );

            for (let i = 0; i < maxRecipients && notificationModel.emailSet.length > 0; ++i) {
                if (notificationModel.emailSet[i]) {
                    recipientEntries.push(
                        new RecipientModel(notificationModel.emailSet[i].faxEmail.value, this.commCategory),
                    );
                }
            }

            if (materialSet && materialSet.length > 0) {
                materialSet.forEach((material: MaterialCheckModel) => {
                    materialEntries.push(
                        new CseMaterialModel(
                            material.materialNr.value,
                            material.quantity.value,
                            '',
                            notificationModel.processIndicator,
                        ),
                    );
                });
            }
        }

        this.materialList = <CseMaterialListModel>(
            this.set(new CseMaterialListModel('QuoteMatOrderPartSet', materialEntries))
        );
        this.equipments = new DropdownModel<TextModel>('Equipment', equipmentDropdownEntries);
        this.equipments.selectByIndex(0);

        modelOptionsLoader.load(this.equipments, 'MaterialQuote', 'Equipment');
        // additional check to only allow to show cse recipient email if it exists
        if (configService.getCseEmailFax()) {
            // Add CSE Email in textbox
            recipientEntries.push(
                new RecipientModel(configService.getCseEmailFax(), this.commCategory, true, true, false),
            );
        }
        this.existingRecipients = <RecipientListModel>(
            this.set(new RecipientListModel('QuoteEmailFaxSet', recipientEntries))
        );
    }

    /**
     * Update the Technical Ident Number in the UI when different equipment is selected from the dropdown
     */
    public updateTechIdentNumber(): void {
        if (this.notificationModel) {
            const equipment = this.notificationModel.equipmentSet.filter(
                (option) => this.equipments.selectedEntry.value === option.equnr.value,
            );
            this.techIdentNumber.value = equipment[0].tidnr.value;
        }
    }

    /**
     * Overwrite getOutModel of ComplexModel to send string output in outmodel
     *
     * @returns {object} outModel for SAP
     */

    public getOutModel(): ODataMaterialQuoteOutModel {
        const outModel = <ODataMaterialQuoteOutModel>super.getOutModel();

        outModel.TimeUsedMinutes = (Number(outModel.TimeUsedMinutes) * ConfigConstants.timeMultiplier).toString();
        outModel.TimeNeedMinutes = (Number(outModel.TimeNeedMinutes) * ConfigConstants.timeMultiplier).toString();

        outModel.Kilometer = outModel.Kilometer !== null ? outModel.Kilometer.toString() : '';

        return outModel;
    }

    public toJSON(): unknown {
        return {
            timeUsed: this.timeUsed.value,
            timeNeed: this.timeNeed.value,
            distance: this.distance.value,
            quoteType: this.quoteTypes.selectedEntry ? this.quoteTypes.selectedEntry.value : '',
            contactPerson: this.contactPerson.value,
            equipment: this.equipments.selectedEntry ? this.equipments.selectedEntry.value : '',
            lTextSet: this.lTextSet.value,
            recipients: this.existingRecipients.value,
            materials: this.materialList.value,
            notificationIdentifier: this.notificationModel ? this.notificationModel.identifier : '',
        };
    }
}

export class MaterialQuoteModelFromJson extends MaterialQuoteModel {
    public constructor(data: JsonMaterialQuoteModel) {
        const notificationService = CustomInjector.getInjector().get(NotificationService);
        const notification = data.notificationIdentifier
            ? notificationService.getNotificationByIdentifier(
                  new NotificationIdentifierModelFromJson(data.notificationIdentifier),
              )
            : undefined;

        super(undefined, notification);

        this.timeUsed.value = data.timeUsed;
        this.timeNeed.value = data.timeNeed;
        this.distance.value = data.distance;
        this.contactPerson.value = data.contactPerson;
        this.lTextSet.value = data.lTextSet;

        if (data.quoteType) {
            this.quoteTypes.selectByValue(data.quoteType);
        }

        this.existingRecipients.value = data.recipients.map(
            (recipient: JsonRecipientModel) =>
                new RecipientModel(
                    recipient.faxEmail.value,
                    recipient.commCategory.value,
                    <boolean>recipient.isSelected.model.value,
                    recipient.isEditable,
                    recipient.isDeletable,
                ),
        );

        const process = notification ? notification.processIndicator : ConfigConstants.PROCESS_INDICATOR.INVIVO;
        this.materialList.value = data.materials.map(
            (cseMaterial) => new CseMaterialModel(cseMaterial.material.value, cseMaterial.quantity.value, '', process),
        );

        this.equipments.selectByValue(data.equipment);
    }
}

export interface JsonMaterialQuoteModel {
    timeUsed: number | undefined;
    timeNeed: number | undefined;
    distance: number | undefined;
    quoteType: string;
    contactPerson: string;
    equipment: string;
    lTextSet: string;
    recipients: JsonRecipientModel[];
    materials: JsonCseMaterialModel[];
    notificationIdentifier: JsonNotificationIdentifierModel | '';
}

export interface ODataMaterialQuoteOutModel extends ODataObject {
    FrontId: string;
    TimeUsedMinutes: string;
    TimeNeedMinutes: string;
    Kilometer: string;
    QuotationType: string;
    ContactPerson: string;
    QuotationNo: string;
    MatQuoteLtextSet: ODataTextLineOutModel[];
    NotificationNo: string;
    TechnicalIdentNo: string;
    QuoteMatOrderPartSet: ODataCseMaterialOutModel[];
    QuoteEmailFaxSet: ODataRecipientOutModel[];
}
